import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  DeleteSweep as DeleteSweepIcon,
  CloudUpload as CloudUploadIcon,
  Storefront as StorefrontIcon,
  WarningAmberRounded as WarningIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import BreakGlassDialog from 'components/BreakGlass/BreakGlassDialog';
import { getLoggedInUserDetails, sendBreakGlassLog } from 'utils/breakGlass';
import { downloadJson, toFileNameSegment } from 'utils/downloadJson';

const ACCENT = '#FF8A00';
const DEFAULT_PROVIDERS = ['Clover', 'Square', 'Toast'];

/**
 * Reusable Import Menu view (UI + flow). Request behaviour is injected via
 * the `onImport` and `onEmpty` callbacks so the same UI can target different
 * backends (POS menu vs. regular menu).
 *
 * @param {string} businessUuid
 * @param {string} breakGlassPage              break-glass log "page" identifier
 * @param {string} breakGlassEventType         break-glass log event type
 * @param {string} emptyActionSummary          human readable action summary
 * @param {(parsedMenu: object) => Promise} onImport   throws on failure
 * @param {() => Promise} onEmpty              throws on failure
 * @param {() => Promise<object>} [onExport]   resolves with the menu JSON in
 *                                             the same shape `onImport` accepts;
 *                                             the export card is hidden when omitted
 * @param {string} [exportFileType]            file name segment, e.g. 'pos-menu'
 * @param {string} [businessName]              file name segment
 */
const ImportMenuView = ({
  businessUuid,
  breakGlassPage,
  breakGlassEventType = 'menu-import-action',
  emptyActionSummary = 'Empty menu for this business',
  onImport,
  onEmpty,
  onExport,
  exportFileType,
  businessName,
  title = 'Import Menu',
  subtitle = 'Import a menu from a JSON file, start from an empty menu, or connect a POS provider.',
  importSuccessMessage = 'Menu imported successfully.',
  emptySuccessMessage = 'Menu emptied successfully.',
  comingSoonProviders = DEFAULT_PROVIDERS,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const loggedInUser = useSelector(({ user }) => user.data);
  const loggedInUserDetails = getLoggedInUserDetails(loggedInUser);

  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedMenu, setParsedMenu] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [breakGlassOpen, setBreakGlassOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const activeStep = parsedMenu ? 1 : 0;
  const cardColumns = onExport ? 4 : 6;

  const ensureBusiness = () => {
    if (!businessUuid) {
      toast.error(t('No business selected'));
      return false;
    }
    return true;
  };

  // Step 1: choose / upload a JSON file (parsed and held in state, not sent yet)
  const handleChooseFile = () => {
    if (!ensureBusiness()) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers change
    event.target.value = '';
    if (!file) return;
    if (!ensureBusiness()) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      try {
        parsed = JSON.parse(e.target.result);
      } catch {
        toast.error(t('Invalid JSON file'));
        setFileName('');
        setParsedMenu(null);
        return;
      }
      setFileName(file.name);
      setParsedMenu(parsed);
    };
    reader.onerror = () => {
      toast.error(t('Failed to read file'));
    };
    reader.readAsText(file);
  };

  const handleClearFile = () => {
    setFileName('');
    setParsedMenu(null);
  };

  // Step 2: import the previously uploaded file
  const handleImport = async () => {
    if (!ensureBusiness()) return;
    if (!parsedMenu) {
      toast.error(t('Please upload a JSON file first'));
      return;
    }
    try {
      setSubmitting(true);
      await onImport(parsedMenu);
      handleClearFile();
      setSuccessMessage(t(importSuccessMessage));
    } catch {
      // Error toast handled by the request interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    if (!ensureBusiness()) return;
    try {
      setExporting(true);
      const menu = await onExport();
      const fileName = [
        toFileNameSegment(businessName),
        exportFileType,
        moment().format('YYYY-MM-DD'),
      ].join('-');
      downloadJson(menu, `${fileName}.json`);
    } catch {
      toast.error(t('Failed to export menu'));
    } finally {
      setExporting(false);
    }
  };

  const handleEmptyClick = () => {
    if (!ensureBusiness()) return;
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };

  const handleConfirmEmpty = () => {
    setConfirmOpen(false);
    setBreakGlassOpen(true);
  };

  const handleAuthorized = (employeeName) => {
    if (!ensureBusiness()) return;
    setSubmitting(true);
    Promise.resolve(onEmpty())
      .then(() => {
        sendBreakGlassLog(
          {
            eventType: breakGlassEventType,
            employeeName,
            actionSummary: emptyActionSummary,
            result: 'success',
            loggedInUser: loggedInUserDetails,
          },
          breakGlassPage
        );
        setSuccessMessage(t(emptySuccessMessage));
      })
      .catch(() => {
        sendBreakGlassLog(
          {
            eventType: breakGlassEventType,
            employeeName,
            actionSummary: emptyActionSummary,
            result: 'failed',
            loggedInUser: loggedInUserDetails,
          },
          breakGlassPage
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2733' }}>
          {t(title)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#6B7280', mt: 0.5 }}>
          {t(subtitle)}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Import JSON menu */}
        <Grid item xs={12} md={cardColumns}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              border: '1px solid #E5E7EB',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 138, 0, 0.12)',
                    color: ACCENT,
                  }}
                >
                  <CloudUploadIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('Import JSON menu')}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 2.5 }}>
                {t(
                  'Upload a .json menu file to map and import it into this business.'
                )}
              </Typography>

              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  mb: 3,
                  '& .MuiStepIcon-root.Mui-active': { color: ACCENT },
                  '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
                }}
              >
                <Step>
                  <StepLabel>{t('Upload file')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('Import')}</StepLabel>
                </Step>
              </Stepper>

              <input
                ref={fileInputRef}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              {!parsedMenu ? (
                <Box
                  onClick={
                    !submitting && businessUuid ? handleChooseFile : undefined
                  }
                  sx={{
                    border: '2px dashed #D1D5DB',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor:
                      submitting || !businessUuid ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                    opacity: !businessUuid ? 0.6 : 1,
                    '&:hover': {
                      borderColor:
                        submitting || !businessUuid ? '#D1D5DB' : ACCENT,
                      backgroundColor:
                        submitting || !businessUuid
                          ? 'transparent'
                          : 'rgba(255, 138, 0, 0.04)',
                    },
                  }}
                >
                  <UploadFileIcon sx={{ color: '#9CA3AF', fontSize: 32 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: '#374151', fontWeight: 500, mt: 1 }}
                  >
                    {t('Click to choose a JSON file')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                    {t('Accepted format: .json')}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    backgroundColor: '#F9FAFB',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(34, 197, 94, 0.12)',
                      color: '#16A34A',
                      flexShrink: 0,
                    }}
                  >
                    <FileIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#374151',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fileName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#16A34A',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14 }} />
                      {t('Ready to import')}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleClearFile}
                    disabled={submitting}
                    aria-label={t('Remove file')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={handleChooseFile}
                  disabled={submitting || !businessUuid}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {parsedMenu ? t('Replace') : t('Upload file')}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  startIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  onClick={handleImport}
                  disabled={submitting || !businessUuid || !parsedMenu}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: ACCENT,
                    '&:hover': { backgroundColor: '#E67C00' },
                  }}
                >
                  {t('Import')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Export JSON menu */}
        {onExport && (
          <Grid item xs={12} md={cardColumns}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: '1px solid #E5E7EB',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 138, 0, 0.12)',
                      color: ACCENT,
                    }}
                  >
                    <FileDownloadIcon />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('Export JSON menu')}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: '#6B7280', mb: 2.5, minHeight: 40 }}
                >
                  {t(
                    'Download the current menu as a .json file that can be imported back.'
                  )}
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  startIcon={
                    exporting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                  onClick={handleExport}
                  disabled={exporting || submitting || !businessUuid}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: ACCENT,
                    '&:hover': { backgroundColor: '#E67C00' },
                  }}
                >
                  {t('Export JSON')}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Empty menu (danger zone) */}
        <Grid item xs={12} md={cardColumns}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              border: '1px solid #FCA5A5',
              borderRadius: 3,
              backgroundColor: 'rgba(239, 68, 68, 0.02)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(239, 68, 68, 0.12)',
                    color: '#DC2626',
                  }}
                >
                  <WarningIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('Empty menu')}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6B7280', mb: 2.5, minHeight: 40 }}
              >
                {t(
                  'Remove all menu data for this business. This action requires break-glass authorization and cannot be undone.'
                )}
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleEmptyClick}
                disabled={submitting || !businessUuid}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('Empty menu')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Import from POS providers */}
        {comingSoonProviders.length > 0 && (
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <StorefrontIcon sx={{ color: '#6B7280' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('Import from POS provider')}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                {t('Direct provider integrations are coming soon.')}
              </Typography>
              <Divider sx={{ my: 2.5 }} />
              <Grid container spacing={2}>
                {comingSoonProviders.map((provider) => (
                  <Grid item xs={12} sm={4} key={provider}>
                    <Box
                      sx={{
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#374151' }}
                      >
                        {t(`Import from ${provider}`)}
                      </Typography>
                      <Chip
                        label={t('Soon')}
                        size="small"
                        sx={{
                          backgroundColor: '#E5E7EB',
                          color: '#6B7280',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('Confirm Empty Menu')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('This will remove all menu data for this business.')}
          </DialogContentText>
          <DialogContentText>
            {t(
              'We will record this action and send the information to management. Are you sure you want to do that?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseConfirm} sx={{ textTransform: 'none' }}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disableElevation
            onClick={handleConfirmEmpty}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <BreakGlassDialog
        open={breakGlassOpen}
        onClose={() => setBreakGlassOpen(false)}
        onAuthorized={handleAuthorized}
        page={breakGlassPage}
        actionSummary={emptyActionSummary}
        description="Enter the employee name and break-glass code to authorize emptying the menu."
      />

      <Dialog
        open={Boolean(successMessage)}
        onClose={() => setSuccessMessage('')}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(34, 197, 94, 0.12)',
              color: '#16A34A',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('Success')}
          </Typography>
          <DialogContentText sx={{ color: '#6B7280' }}>
            {successMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setSuccessMessage('')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              backgroundColor: ACCENT,
              '&:hover': { backgroundColor: '#E67C00' },
            }}
          >
            {t('Done')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportMenuView;
